import React, { useRef, useState, useEffect } from 'react'
import { motion, useInView, AnimatePresence, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Check } from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'
import { HudsonOfferBanner } from '../components/marketing/MarketingChrome'

const ease = [0.16, 1, 0.3, 1] as const
const heroEase = [0.76, 0, 0.24, 1] as const

const brand = {
  black: '#0A0B0B',
  white: '#F7F8F6',
  tapeTurquoise: '#0D3135',
  utilityBlue: '#145F6C',
  drillbitSkies: '#4A7D83',
  sandrift: '#AB9381',
  plaster: '#CECAC3',
} as const

const BOOKING_CALENDAR_URL = 'https://api.leadconnectorhq.com/widget/booking/vGBU75grNpdx4TI2SvX2'

const MeetCalendarCta: React.FC<{ size?: 'md' | 'lg' }> = ({ size = 'md' }) => {
  const pad = size === 'lg' ? 'px-14 py-6' : 'px-10 py-5'
  return (
    <a
      href={BOOKING_CALENDAR_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`group inline-flex items-center justify-center gap-3 text-white ${pad} text-[15px] font-medium font-sans tracking-[0.06em] uppercase transition-all duration-500 hover:brightness-110`}
      style={{ backgroundColor: brand.sandrift }}
    >
      Schedule a Meeting <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
    </a>
  )
}

const ArchetypeQuizCta: React.FC<{ size?: 'md' | 'lg'; lightOnDark?: boolean }> = ({ size = 'md', lightOnDark = false }) => {
  const pad = size === 'lg' ? 'px-14 py-6' : 'px-10 py-5'
  if (lightOnDark) {
    return (
      <Link
        to="/quiz"
        className={`group inline-flex items-center justify-center gap-3 ${pad} text-[15px] font-medium font-sans tracking-[0.06em] uppercase transition-all duration-500 border border-white/25 text-white/95 hover:bg-white/10`}
      >
        Discover Your Archetype <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
      </Link>
    )
  }
  return (
    <Link
      to="/quiz"
      className={`group inline-flex items-center justify-center gap-3 ${pad} text-[15px] font-medium font-sans tracking-[0.06em] uppercase transition-all duration-500 hover:opacity-70`}
      style={{ border: `1px solid ${brand.plaster}`, color: brand.drillbitSkies }}
    >
      Discover Your Archetype <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
    </Link>
  )
}

const REMOTE_IMG = {
  finalCtaFamily: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/babywithhandsovermom.jpg',
  baileyTestimonial: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/bailey-p-testimonial.png',
  testimonialPattern: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/huson-pattern-landscape.png',
  kelseyTestimonial: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/kelsey-s-testimonial.png',
  taylorTestimonial: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/taylor-g-testimonial.png',
} as const

const MEMBER_TESTIMONIALS = [
  {
    name: 'Bailey P.',
    place: 'Ankeny, Iowa',
    quote:
      'Hudson has been so wonderful. There have been several things that have come up and my husband has said let\u2019s just add it to Hudson\u2019s list.',
    image: REMOTE_IMG.baileyTestimonial,
  },
  {
    name: 'Kelsey S.',
    place: 'Johnston',
    quote:
      'We used to spend every weekend chipping away at a list that never shrank. Now someone who actually knows our home is ahead of it \u2014 filters, gutters, the stuff we always meant to get to.',
    image: REMOTE_IMG.kelseyTestimonial,
  },
  {
    name: 'Taylor G.',
    place: 'West Des Moines',
    quote:
      'One person. The whole house. When something comes up, we don\u2019t spiral — we message Hudson and it\u2019s handled. It feels strangely calm for homeownership.',
    image: REMOTE_IMG.taylorTestimonial,
  },
] as const

const img = {
  hero: '/homepage-assets/hero-page1.png',
  phone: '/homepage-assets/page6.png',
  hudsonAtDoor: '/illustrations/hudson-at-door.png',
  clipboardChecklist: '/illustrations/clipboard-checklist.png',
  hudsonInCar: '/illustrations/hudson-in-car.png',
  hudsonInspecting: '/illustrations/hudson-inspecting.png',
  houseCross2: '/illustrations/house-crosssection-2.png',
  houseCross3: '/illustrations/house-crosssection-3.png',
  iconClock: '/illustrations/icon-clock.png',
  watchLuxury: '/illustrations/watch-luxury.png',
  houseEmbossColonial: '/illustrations/house-emboss-colonial.png',
  houseEmbossModern: '/illustrations/house-emboss-modern.png',
  houseEmbossEstate: '/illustrations/house-emboss-estate.png',
  logoLetterpress: '/illustrations/logo-letterpress.png',
  reportCard: '/illustrations/report-card.png',
  penBlueprint: '/illustrations/pen-blueprint.png',
  houseSketchColonial: '/illustrations/house-sketch-colonial.png',
  hudsonConsultation: '/illustrations/hudson-consultation.png',
  curtainCleaning: '/curtaincleaning.png',
  page4: '/homepage-assets/page4.png',
  coupleGolfing: '/illustrations/couple-golfing.png',
  hudsonHvac: '/illustrations/hudson-hvac.png',
  myhomeScoreReport: '/illustrations/myhome-score-report.png',
} as const

const PAPER = '#F0EDE8'
const DARK = '#0B272C'

const blendMask = (shape: string) => ({
  WebkitMaskImage: shape,
  maskImage: shape,
})


const MaskedLine: React.FC<{
  children: React.ReactNode; delay: number; className?: string; style?: React.CSSProperties
}> = ({ children, delay, className = '', style }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: '115%' }}
      animate={{ y: '0%' }}
      transition={{ duration: 1.2, delay, ease: heroEase }}
      className={className}
      style={style}
    >{children}</motion.div>
  </div>
)

const Reveal: React.FC<{
  children: React.ReactNode; delay?: number; y?: number; className?: string
}> = ({ children, delay = 0, y = 50, className = '' }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease, delay }}
      className={className}
    >{children}</motion.div>
  )
}

const ScreenedBg: React.FC<{
  src: string; blend?: string; opacity?: number; className?: string; style?: React.CSSProperties
}> = ({ src, blend = 'multiply', opacity = 0.5, className = '', style }) => (
  <img
    src={src} alt="" aria-hidden="true"
    className={`pointer-events-none select-none ${className}`}
    style={{ mixBlendMode: blend as React.CSSProperties['mixBlendMode'], opacity, ...style }}
  />
)

/* ══════════════════════════════════════════════════
   1. HERO — Light cinematic, family photo expands on scroll,
   paper-cut sketch faded into background
   ══════════════════════════════════════════════════ */
const Hero: React.FC = () => {
  const [ready, setReady] = useState(false)
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] })
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.35])
  const embossOpacity = useTransform(scrollYProgress, [0, 0.4], [0.06, 0.12])
  const textFade = useTransform(scrollYProgress, [0, 0.3], [1, 0])
  const textY = useTransform(scrollYProgress, [0, 0.4], ['0%', '20%'])
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.5], [0.55, 0.3])
  useEffect(() => { setReady(true) }, [])

  return (
    <section ref={ref} className="relative h-[105svh] min-h-[860px] flex flex-col overflow-hidden" style={{ backgroundColor: PAPER }}>
      {/* Family photo — expands on scroll */}
      <div className="absolute inset-0" aria-hidden>
        <motion.div className="absolute inset-[-15%]" style={{ scale: imgScale }}>
          <motion.img
            src={img.hero} alt=""
            initial={{ scale: 1.12, opacity: 0 }}
            animate={ready ? { scale: 1, opacity: 1 } : {}}
            transition={{ duration: 3, ease }}
            className="h-full w-full object-cover object-[center_12%]"
          />
        </motion.div>
      </div>

      {/* Warm light overlay — left side heavy for text, photo peeks through right */}
      <motion.div className="absolute inset-0" style={{ opacity: overlayOpacity }} aria-hidden>
        <div className="absolute inset-0" style={{ background: `linear-gradient(105deg, ${PAPER}f0 0%, ${PAPER}e0 35%, ${PAPER}80 55%, ${PAPER}40 72%, transparent 100%)` }} />
      </motion.div>
      <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${PAPER}e8 0%, transparent 18%, transparent 75%, ${PAPER}f5 100%)` }} aria-hidden />

      {/* House emboss — subtle watermark texture behind text */}
      <div className="absolute inset-0 hidden lg:block pointer-events-none" aria-hidden>
        <motion.div className="absolute top-1/2 left-[12%] -translate-y-[45%] w-[600px] xl:w-[700px]" style={{ opacity: embossOpacity }}>
          <ScreenedBg src={img.houseEmbossColonial} blend="multiply" opacity={1} className="w-full h-auto" style={blendMask('radial-gradient(ellipse 50% 50% at 50% 50%, black 10%, transparent 55%)')} />
        </motion.div>
      </div>

      {/* Content — left-aligned editorial layout */}
      <motion.div className="relative z-10 flex-1 flex items-center pt-20 lg:pt-24" style={{ opacity: textFade, y: textY }}>
        <div className="px-6 md:px-10 lg:px-16 xl:px-24 w-full max-w-[1400px]">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease }}
            className="font-sans text-xs sm:text-sm font-semibold uppercase tracking-[0.4em] mb-8 lg:mb-12"
            style={{ color: brand.sandrift }}
          >
            Private Home Management
          </motion.p>

          <div className="max-w-4xl">
            <MaskedLine delay={0.6} className="font-serif font-normal" style={{ fontSize: 'clamp(3.2rem, 9vw, 8rem)', lineHeight: 0.88, letterSpacing: '-0.04em', color: brand.tapeTurquoise }}>We Maintain</MaskedLine>
            <MaskedLine delay={0.8} className="font-serif font-normal" style={{ fontSize: 'clamp(3.2rem, 9vw, 8rem)', lineHeight: 0.88, letterSpacing: '-0.04em', color: brand.tapeTurquoise }}>Your Home</MaskedLine>
          </div>

          <motion.div initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }} transition={{ duration: 1.4, delay: 1.1, ease }} className="h-px w-20 lg:w-36 my-7 lg:my-9 origin-left" style={{ backgroundColor: brand.sandrift }} />

          <MaskedLine delay={1.3} className="font-serif italic" style={{ fontSize: 'clamp(1.4rem, 3.5vw, 2.5rem)', lineHeight: 1.15, letterSpacing: '-0.02em', color: brand.sandrift }}>So You Don&apos;t Have To</MaskedLine>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 1.9 }} className="mt-7 lg:mt-9 text-base sm:text-lg leading-[1.85] max-w-md font-sans" style={{ color: brand.drillbitSkies }}>
            You&apos;re managing the kids, the schedules, the everything. Your house shouldn&apos;t be one more thing on the list.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 2.3 }} className="mt-9 lg:mt-12">
            <div className="flex flex-col sm:flex-row gap-4">
              <MeetCalendarCta />
              <ArchetypeQuizCta />
            </div>
            <p className="text-sm mt-4 font-sans" style={{ color: brand.drillbitSkies }}>No commitment required. Cancel anytime.</p>
          </motion.div>
        </div>
      </motion.div>
    </section>
  )
}

/* ══════════════════════════════════════════════════
   TRANSITION HELPERS
   ══════════════════════════════════════════════════ */
const SketchBreak: React.FC<{ bg: string }> = ({ bg }) => {
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
  const y = useTransform(scrollYProgress, [0, 1], ['5%', '-5%'])
  return (
    <section ref={ref} className="relative overflow-hidden py-4" style={{ backgroundColor: bg }}>
      <motion.div className="relative w-full" style={{ y }}>
        <ScreenedBg src={img.houseSketchColonial} blend="multiply" opacity={0.35} className="w-full h-auto" style={blendMask('linear-gradient(to bottom, transparent 2%, black 18%, black 82%, transparent 98%)')} />
      </motion.div>
    </section>
  )
}

/* ══════════════════════════════════════════════════
   2. PROBLEM — Bullet points for skimmability.
   Gradient from PAPER at top for smooth transition.
   ══════════════════════════════════════════════════ */
const PAIN_POINTS = [
  'The furnace filter you keep forgetting to change',
  'The gutter cleaning you scheduled in your head three months ago',
  'The handyman you called twice who never called back',
  'The list on the fridge that only gets longer',
  'The weekend project that ate your only free Saturday',
] as const

const Problem: React.FC = () => (
  <section className="relative overflow-hidden" style={{ backgroundColor: DARK }}>
    <div className="absolute top-0 left-0 right-0 h-48" style={{ background: `linear-gradient(to bottom, ${PAPER}, ${DARK})` }} aria-hidden />
    <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse 70% 55% at 50% 30%, ${brand.utilityBlue}18, transparent)` }} aria-hidden />
    <div className="relative z-10 max-w-[1000px] mx-auto px-6 md:px-10 lg:px-16 pt-48 md:pt-60 lg:pt-72 pb-36 md:pb-44 lg:pb-52">
      <Reveal>
        <h2 className="font-serif font-normal text-white text-center" style={{ fontSize: 'clamp(2.75rem, 6vw, 4.75rem)', lineHeight: 1.05, letterSpacing: '-0.03em' }}>
          You&apos;re Already Managing{' '}<span className="italic" style={{ color: brand.sandrift }}>Everything Else</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-10 lg:mt-14 font-sans text-lg lg:text-xl leading-[1.85] text-white/80 text-center max-w-2xl mx-auto">
          Three kids. Two schedules. A house that needs more than you have time to give it. You spend your free time coordinating vendors, tracking maintenance, and reacting when something breaks.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <ul className="mt-12 lg:mt-16 space-y-6 max-w-xl mx-auto">
          {PAIN_POINTS.map((p, i) => (
            <li key={i} className="flex gap-4 items-start">
              <span className="mt-2.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: brand.sandrift }} />
              <span className="font-sans text-lg leading-relaxed text-white/80">{p}</span>
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-12 lg:mt-16 font-sans text-lg lg:text-xl leading-[1.85] text-white/80 text-center max-w-2xl mx-auto">
          The to-do list never ends. The mental load never lifts. And your home is always sitting in the back of your mind.
        </p>
      </Reveal>
      <Reveal delay={0.25}>
        <p className="mt-8 font-sans text-lg lg:text-xl leading-[1.85] text-white/80 text-center max-w-2xl mx-auto">
          When you finally bring it up, you both know it needs to get done. Neither of you wants to be the one to do it.
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-14 h-px w-24 mx-auto" style={{ backgroundColor: brand.sandrift }} />
      </Reveal>
      <Reveal delay={0.35}>
        <p className="mt-14 font-serif italic leading-[1.3] text-center" style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)', color: brand.sandrift }}>
          Somewhere along the way, the home you worked hard for started feeling like a second job.
        </p>
      </Reveal>
    </div>
  </section>
)

/* ══════════════════════════════════════════════════
   3. GUIDE — Empathy + Authority (three pillars)
   No "Meet Your Guide" label — the visitor shouldn't
   see the StoryBrand scaffolding.
   ══════════════════════════════════════════════════ */
const PILLARS = [
  { title: 'We Do the Work. We Know Who to Call.', body: 'Your Hudson handles the bulk of maintenance in your home\u2014seasonal prep, checklists, and the jobs that don\u2019t need a trade license. When something needs a licensed specialist, we bring in partners we already trust, schedule them, and follow through. You\u2019re not the middleman.' },
  { title: 'Proactive. Not When It Breaks.', body: 'Your HVAC gets serviced before the first cold night. Your gutters get cleared before the storm. Problems caught in hours, not discovered in thousands.' },
  { title: 'Your Terms. Your Privacy.', body: "We work around your life, not the other way around. No service windows. No strangers. Just a home that\u2019s quietly, professionally maintained." },
] as const

const Guide: React.FC = () => (
  <section className="relative overflow-hidden" style={{ backgroundColor: brand.tapeTurquoise }}>
    <div className="absolute bottom-0 left-0 right-0 h-40" style={{ background: `linear-gradient(to bottom, ${brand.tapeTurquoise}, ${PAPER})` }} aria-hidden />
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-40 lg:py-56">
      {/* Sticky empathy left / scrolling pillars right on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
        <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
          <Reveal>
            <p className="font-sans text-lg lg:text-xl leading-[1.9] text-white/80">
              We&apos;ve been inside hundreds of homes where everything looks fine from the outside &mdash; but inside there&apos;s a growing list of things nobody has time to get to. We know what&apos;s been piling up because we see it every single day.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 h-px w-20" style={{ backgroundColor: brand.sandrift }} />
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 font-serif italic leading-[1.3]" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', letterSpacing: '-0.02em', color: brand.sandrift }}>
              Hudson assigns one dedicated professional to your property. They learn every system, do the core work themselves, and bring in trusted specialists when the job demands it\u2014fully coordinated so you stay out of the middle.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-7 space-y-16 lg:space-y-20">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12}>
              <div className="p-8 lg:p-12" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="w-14 h-px mb-8" style={{ backgroundColor: brand.sandrift }} />
                <h3 className="font-serif font-normal text-white mb-5" style={{ fontSize: 'clamp(1.6rem, 2.8vw, 2rem)', lineHeight: 1.2, letterSpacing: '-0.02em' }}>{p.title}</h3>
                <p className="font-sans text-base lg:text-lg leading-[1.85] text-white/75">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
)

/* ══════════════════════════════════════════════════
   4. PLAN — Detailed process so people know exactly
   what to expect. Joe's #1 priority.
   ══════════════════════════════════════════════════ */
const STEPS = [
  {
    num: '01',
    title: 'One Conversation',
    body: 'Book a 15-minute call online. We\u2019ll learn about your home, what\u2019s been on your mind, and answer every question you have. No pitch, no pressure \u2014 just a real conversation about what your home needs.',
    detail: 'Your Hudson walks you through how everything works, what\u2019s included, and whether it\u2019s a fit. Think a short visual overview\u2014about fifteen minutes\u2014so you can see the picture clearly. Education, not a sales deck.',
    illustration: img.hudsonConsultation,
  },
  {
    num: '02',
    title: 'A Plan Built for Your Property',
    body: 'Your Hudson comes to your home and walks every room, every system \u2014 HVAC, water heater, gutters, caulking, filters \u2014 everything most homeowners don\u2019t know to look for. We document what we find and turn it into your customized maintenance roadmap\u2014refined with you after the visit, not rushed at the door.',
    detail: 'Priorities, schedules, and cost picture\u2014based on your home\u2019s size, age, and what\u2019s been deferred. No surprises and no hidden-fee follow-ups. For members, this full-home assessment and roadmap are part of your first month\u2014your onboarding, not a separate upsell. Booked on its own, that assessment and plan are a $1,495 value; as a member, they\u2019re included in month one.',
    illustration: img.hudsonInspecting,
  },
  {
    num: '03',
    title: 'Your Hudson Takes Over',
    body: 'Once you\u2019re onboarded, your Hudson leads the work in your home\u2014monthly visits, seasonal tasks, and the running list\u2014and schedules licensed pros when your property needs them. One person owns the plan end to end.',
    detail: 'You\u2019ll get a summary of what\u2019s happening and when. That\u2019s your only involvement. From here, your Hudson owns it.',
    illustration: img.hudsonInCar,
  },
] as const

const Plan: React.FC = () => (
  <section className="relative overflow-hidden" style={{ backgroundColor: PAPER }}>
    <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-40 lg:py-56">
      <div className="text-center mb-16 lg:mb-24">
        <Reveal>
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-6" style={{ color: brand.sandrift }}>Here&apos;s Exactly What Happens</p>
          <h2 className="font-serif font-normal max-w-3xl mx-auto" style={{ fontSize: 'clamp(2.5rem, 5.5vw, 4.25rem)', lineHeight: 1.05, letterSpacing: '-0.03em', color: brand.tapeTurquoise }}>
            Three Steps. <span className="italic" style={{ color: brand.sandrift }}>Then You&apos;re Done.</span>
          </h2>
          <p className="mt-6 font-sans text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: brand.drillbitSkies }}>
            No mystery. No runaround. This is the entire process from first call to &ldquo;I don&apos;t think about my house anymore.&rdquo;
          </p>
        </Reveal>
      </div>

      <div className="space-y-0">
        {STEPS.map((s, i) => (
          <Reveal key={s.num} delay={0.1 + i * 0.08}>
            <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-14 items-start py-14 lg:py-18" style={i < STEPS.length - 1 ? { borderBottom: `1px solid ${brand.plaster}40` } : {}}>
              <div className="lg:sticky lg:top-32 lg:self-start">
                <span className="font-serif font-normal select-none block" style={{ fontSize: 'clamp(4.5rem, 9vw, 7rem)', lineHeight: 0.8, letterSpacing: '-0.05em', color: `${brand.sandrift}40` }}>{s.num}</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
                <div>
                  <div className="w-14 h-px mb-6" style={{ backgroundColor: brand.sandrift }} />
                  <h3 className="font-serif font-normal mb-4" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', lineHeight: 1.08, letterSpacing: '-0.025em', color: brand.tapeTurquoise }}>{s.title}</h3>
                  <p className="font-sans text-lg leading-[1.85] mb-4" style={{ color: brand.tapeTurquoise, opacity: 0.85 }}>{s.body}</p>
                  <p className="font-sans text-[15px] leading-[1.85]" style={{ color: brand.drillbitSkies, opacity: 0.8 }}>{s.detail}</p>
                </div>
                <div className="relative aspect-[4/3] lg:aspect-square">
                  <ScreenedBg src={s.illustration} blend="multiply" opacity={0.7} className="w-full h-full object-contain" style={blendMask('radial-gradient(ellipse 50% 50% at 50% 50%, black 25%, transparent 65%)')} />
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.5} className="mt-20 lg:mt-28 text-center">
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center">
          <MeetCalendarCta />
          <ArchetypeQuizCta />
        </div>
        <p className="text-sm mt-3 font-sans" style={{ color: brand.drillbitSkies }}>Pick a time on our calendar. 15 minutes. No commitment. No pitch.</p>
        <p className="text-sm mt-1 font-sans" style={{ color: brand.drillbitSkies }}>Try it for 30 days. Full refund if it doesn&apos;t change how your home runs.</p>
      </Reveal>
    </div>
  </section>
)

/* ══════════════════════════════════════════════════
   5. FAILURE — Cost of inaction. Three stats. Sharp.
   Creates urgency right before the pricing section.
   ══════════════════════════════════════════════════ */
const Failure: React.FC = () => (
  <section className="relative overflow-hidden" style={{ backgroundColor: DARK }}>
    <div className="absolute top-0 left-0 right-0 h-48" style={{ background: `linear-gradient(to bottom, ${PAPER}, ${DARK})` }} aria-hidden />
    <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse 70% 55% at 50% 35%, ${brand.utilityBlue}18, transparent)` }} aria-hidden />
    <div className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-10 lg:px-16 pt-48 md:pt-60 lg:pt-72 pb-32 md:pb-40 lg:pb-52 text-center">
      <Reveal>
        <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-6" style={{ color: brand.sandrift }}>The Cost of Waiting</p>
        <h2 className="font-serif font-normal text-white" style={{ fontSize: 'clamp(2.75rem, 6vw, 4.75rem)', lineHeight: 1.05, letterSpacing: '-0.03em' }}>
          What Deferred Maintenance{' '}<span className="italic" style={{ color: brand.sandrift }}>Costs</span>
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-10 mt-16 lg:mt-20 mb-16 lg:mb-20">
        {[
          { cost: '$40/mo', label: 'The forgotten HVAC filter', note: 'Compounding in lost efficiency' },
          { cost: '$3,000', label: 'The gutter you didn\u2019t clean', note: 'Now it\u2019s foundation damage' },
          { cost: '$1,200', label: 'Water heater failure', note: 'Emergency call \u2014 was a $50 flush' },
        ].map((item, i) => (
          <Reveal key={item.label} delay={0.1 + i * 0.08}>
            <div className="p-8 lg:p-10" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <span className="block font-serif mb-4" style={{ fontSize: 'clamp(2.25rem, 4vw, 3.5rem)', letterSpacing: '-0.03em', color: 'white' }}>{item.cost}</span>
              <span className="block font-sans text-base text-white/80 mb-2">{item.label}</span>
              <span className="block font-sans text-sm text-white/60">{item.note}</span>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.3}>
        <p className="font-sans text-lg lg:text-xl text-white/85 font-medium leading-relaxed max-w-2xl mx-auto">Deferred maintenance doesn&apos;t stay deferred. It compounds. And the bill always comes due.</p>
      </Reveal>
    </div>
  </section>
)

/* ══════════════════════════════════════════════════
   6. CALL TO ACTION — Service Comparison Cards
   ══════════════════════════════════════════════════ */
const MEMBERSHIP_FEATURES = [
  'Your own Hudson \u2014 one dedicated professional, year-round',
  'Proactive seasonal maintenance across every major system',
  'MyHome Blueprint\u2122 \u2014 a five-year maintenance roadmap',
  "MyHome Vault\u2122 \u2014 your home\u2019s service history, 24/7",
  'Same-day response. No exceptions.',
  'Annual restocking \u2014 filters, bulbs, batteries',
  'Holiday lights, seasonal cleanouts, priority specialists',
]
const REFRESH_FEATURES = [
  'Mini MyHome Score\u2122 \u2014 top three priorities identified',
  'Visual inspection of two systems of your choice',
  'Seasonal maintenance completed during the visit',
  'On-site walkthrough with actionable recommendations',
  'Summary report \u2014 what matters now, what can wait',
  'Credit toward any future Hudson service',
]
const SMARTSTART_FEATURES = [
  'New locks, rekeyed garage access, fresh detectors',
  'Full systems orientation \u2014 every shutoff, panel, switch',
  'HVAC filters, dryer vents, fridge filter replaced',
  'Under-sink plumbing, caulking, exhaust fans refreshed',
  'Water heater flushed, seals and weatherstripping checked',
  'Complete digital summary with follow-up priorities',
]

const ServiceCards: React.FC = () => (
  <section className="relative overflow-hidden" style={{ backgroundColor: DARK }}>
    <div className="absolute top-0 left-0 right-0 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} aria-hidden />
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-36 md:py-44 lg:py-56">
      <Reveal>
        <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-6" style={{ color: brand.sandrift }}>Your Options</p>
        <h2 className="font-serif font-normal text-white mb-6" style={{ fontSize: 'clamp(2.75rem, 6vw, 4.75rem)', lineHeight: 1.05, letterSpacing: '-0.03em' }}>
          Choose How Your Home Gets{' '}<span className="italic" style={{ color: brand.sandrift }}>Managed</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1}><p className="font-sans text-lg text-white/75 max-w-2xl mb-16 lg:mb-24 leading-relaxed">Full-service management or a single visit. Built for people who make decisions quickly and expect them executed properly.</p></Reveal>

      {/* HERO CARD — Membership */}
      <Reveal delay={0.15}>
        <div className="relative p-10 lg:p-16 border-2 mb-8" style={{ borderColor: brand.sandrift, backgroundColor: 'rgba(255,255,255,0.05)' }}>
          <div className="absolute -top-3 left-10 px-5 py-1.5 font-sans text-xs font-semibold uppercase tracking-[0.2em] text-white" style={{ backgroundColor: brand.sandrift }}>Most Popular</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <h3 className="font-serif text-white mt-2" style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)', letterSpacing: '-0.025em', lineHeight: 1.1 }}>Home Membership</h3>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-serif text-white" style={{ fontSize: 'clamp(3rem, 5vw, 4rem)', letterSpacing: '-0.04em' }}>$399</span>
                <span className="font-sans text-lg text-white/60">/month</span>
              </div>
              <p className="mt-6 font-sans text-lg text-white/80 leading-relaxed max-w-lg">One professional. Your entire home. Nothing falls through. Your Hudson owns every detail &mdash; hands-on maintenance, specialist work when it\u2019s needed, emergencies, and long-term planning.</p>
              <Link to="/membership" className="group mt-10 inline-flex items-center gap-3 px-12 py-5 font-sans text-[15px] font-medium text-white tracking-[0.06em] uppercase transition-all duration-500 hover:brightness-110" style={{ backgroundColor: brand.sandrift }}>
                Discover Membership <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
            <ul className="space-y-5">
              {MEMBERSHIP_FEATURES.map((f) => (
                <li key={f} className="flex gap-4 items-start">
                  <Check className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={2.5} style={{ color: brand.sandrift }} />
                  <span className="font-sans text-base leading-snug text-white/80">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>

      {/* SECONDARY CARDS — Refresh + SmartStart */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { name: 'MyHome Refresh\u2122', price: '$299', unit: 'one-time', desc: 'One visit. A complete picture of where your home stands.', features: REFRESH_FEATURES.slice(0, 4), link: '/refresh' },
          { name: 'MyHome SmartStart\u2122', price: '$299', unit: 'one-time', desc: "You just closed. Your home has history you haven\u2019t met yet.", features: SMARTSTART_FEATURES.slice(0, 4), link: '/smart-start' },
        ].map((c, i) => (
          <Reveal key={c.name} delay={0.2 + i * 0.1}>
            <div className="relative p-8 lg:p-10 h-full flex flex-col" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 className="font-serif text-white" style={{ fontSize: '1.5rem', letterSpacing: '-0.02em' }}>{c.name}</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-serif text-white" style={{ fontSize: '2rem', letterSpacing: '-0.03em' }}>{c.price}</span>
                <span className="font-sans text-sm text-white/60">{c.unit}</span>
              </div>
              <p className="mt-4 font-sans text-base text-white/75 leading-relaxed">{c.desc}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {c.features.map((f) => (
                  <li key={f} className="flex gap-3 items-start">
                    <Check className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} style={{ color: brand.sandrift }} />
                    <span className="font-sans text-sm leading-snug text-white/75">{f}</span>
                  </li>
                ))}
              </ul>
              <Link to={c.link} className="group mt-8 w-full inline-flex items-center justify-center gap-3 px-8 py-4 font-sans text-sm font-medium text-white tracking-[0.06em] uppercase transition-all duration-500 hover:border-white/40" style={{ border: '1px solid rgba(255,255,255,0.15)' }}>
                Learn More <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
)

/* ══════════════════════════════════════════════════
   7. SUCCESS — Transformation + member voices (pattern + card grid)
   ══════════════════════════════════════════════════ */
const TestimonialCard: React.FC<{
  name: string
  place: string
  quote: string
  image: string
  delay: number
}> = ({ name, place, quote, image, delay }) => (
  <Reveal delay={delay}>
    <figure
      className="flex h-full flex-col overflow-hidden rounded-lg shadow-[0_20px_56px_-24px_rgba(13,49,53,0.2)] ring-1 ring-black/[0.07]"
      style={{ backgroundColor: brand.white }}
    >
      <div className="relative aspect-[5/3] w-full shrink-0">
        <img
          src={image}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-[center_45%_top]"
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="flex flex-1 flex-col px-6 py-7 md:px-7 md:py-8">
        <span className="font-serif text-4xl leading-none opacity-[0.12] select-none" style={{ color: brand.tapeTurquoise }} aria-hidden>
          &ldquo;
        </span>
        <blockquote
          className="-mt-4 font-serif italic leading-[1.5]"
          style={{ fontSize: 'clamp(1.05rem, 1.9vw, 1.2rem)', letterSpacing: '-0.015em', color: brand.tapeTurquoise }}
        >
          {quote}
        </blockquote>
        <footer className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-6" style={{ borderColor: `${brand.plaster}80` }}>
          <span className="h-px w-8 shrink-0" style={{ backgroundColor: brand.sandrift }} aria-hidden />
          <cite className="not-italic font-sans text-[12px] font-semibold uppercase tracking-[0.16em]" style={{ color: brand.sandrift }}>
            {name}
            <span className="mx-2 font-normal opacity-50">&middot;</span>
            {place}
          </cite>
        </footer>
      </div>
      <figcaption className="sr-only">
        {name} from {place}: {quote}
      </figcaption>
    </figure>
  </Reveal>
)

const Success: React.FC = () => (
  <section className="relative overflow-hidden" style={{ backgroundColor: PAPER }}>
    <div className="absolute top-0 left-0 right-0 h-48" style={{ background: `linear-gradient(to bottom, ${DARK}, ${PAPER})` }} aria-hidden />

    <div
      className="pointer-events-none absolute inset-0 opacity-[0.16]"
      style={{
        backgroundImage: `url(${REMOTE_IMG.testimonialPattern})`,
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto 96px',
        mixBlendMode: 'multiply',
      }}
      aria-hidden
    />
    <div
      className="pointer-events-none absolute inset-0"
      style={{ background: `linear-gradient(180deg, ${PAPER}00 0%, ${PAPER}e6 55%, ${PAPER} 100%)` }}
      aria-hidden
    />

    <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 pt-40 md:pt-48 lg:pt-56 pb-28 md:pb-36 lg:pb-44">
      <div className="mx-auto max-w-[40rem] text-center lg:mx-0 lg:max-w-[44rem] lg:text-left">
        <Reveal>
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-5" style={{ color: brand.sandrift }}>
            The Transformation
          </p>
          <h2
            className="font-serif font-normal leading-[1.12]"
            style={{ fontSize: 'clamp(2.15rem, 4.5vw, 3.5rem)', letterSpacing: '-0.03em', color: brand.tapeTurquoise }}
          >
            The list on the fridge is gone. The weekends are yours again. The house is running better than it has in years &mdash; and you didn&apos;t lift a finger.
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-8 font-sans text-lg md:text-xl font-medium leading-relaxed max-w-xl mx-auto lg:mx-0" style={{ color: brand.drillbitSkies }}>
            One conversation. That&apos;s all it took.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-12 flex items-center justify-center gap-4 lg:justify-start">
          <div className="h-px w-12 shrink-0" style={{ backgroundColor: brand.sandrift }} aria-hidden />
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.28em]" style={{ color: brand.sandrift }}>
            What members say
          </p>
          <div className="h-px w-12 shrink-0 lg:w-24" style={{ backgroundColor: brand.sandrift }} aria-hidden />
        </div>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-8 xl:grid-cols-3 xl:gap-10">
        {MEMBER_TESTIMONIALS.map((t, i) => (
          <div
            key={t.name}
            className={i === 2 ? 'md:col-span-2 md:flex md:justify-center xl:col-span-1' : ''}
          >
            <div className={i === 2 ? 'w-full max-w-md xl:max-w-none' : ''}>
              <TestimonialCard name={t.name} place={t.place} quote={t.quote} image={t.image} delay={0.12 + i * 0.07} />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
)

/* ══════════════════════════════════════════════════
   8. FINAL CTA — Editorial split + lifestyle image
   ══════════════════════════════════════════════════ */
const FinalCTA: React.FC = () => (
  <section className="relative overflow-hidden" style={{ backgroundColor: PAPER }}>
    <div className="absolute top-0 left-0 right-0 h-40" style={{ background: `linear-gradient(to bottom, ${PAPER}, transparent)` }} aria-hidden />
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.04]"
      style={{
        backgroundImage: `url(/illustrations/texture-paper-warm.png)`,
        backgroundSize: '480px',
        mixBlendMode: 'multiply',
      }}
      aria-hidden
    />
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-36 md:pt-44 lg:pt-52 pb-28 md:pb-36 lg:pb-44">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 xl:gap-20 items-center">
        {/* Lifestyle image — mobile first for tone, desktop right column */}
        <Reveal className="order-1 lg:order-2">
          <div
            className="relative mx-auto w-full max-w-xl lg:max-w-none overflow-hidden rounded-sm shadow-[0_24px_60px_-12px_rgba(13,49,53,0.18)] ring-1 ring-black/[0.06]"
            style={{ aspectRatio: '16 / 10' }}
          >
            <img
              src={REMOTE_IMG.finalCtaFamily}
              alt="Parent with baby at home"
              className="absolute inset-0 h-full w-full object-cover object-[center_35%]"
              sizes="(min-width: 1024px) 50vw, 100vw"
              loading="lazy"
              decoding="async"
            />
            <div
              className="pointer-events-none absolute inset-0 lg:hidden"
              style={{ background: `linear-gradient(to bottom, transparent 55%, ${PAPER} 100%)` }}
              aria-hidden
            />
          </div>
        </Reveal>

        <div className="order-2 lg:order-1 min-w-0">
          <Reveal>
            <div className="max-w-3xl">
              <div className="w-20 h-px mb-10 lg:mb-12" style={{ backgroundColor: brand.sandrift }} />
              <p className="font-serif italic leading-[1.4]" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', color: brand.drillbitSkies }}>
                You take care of everyone. Your home should be the one thing that takes care of itself.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-12 lg:mt-16 xl:mt-20">
              <h2 className="font-serif font-normal" style={{ fontSize: 'clamp(4rem, 10vw, 9rem)', lineHeight: 0.92, letterSpacing: '-0.05em', color: brand.tapeTurquoise }}>
                Everyone
              </h2>
              <h2 className="font-serif font-normal" style={{ fontSize: 'clamp(4rem, 10vw, 9rem)', lineHeight: 0.92, letterSpacing: '-0.05em', color: brand.tapeTurquoise }}>
                Deserves
              </h2>
              <h2 className="font-serif italic" style={{ fontSize: 'clamp(4rem, 10vw, 9rem)', lineHeight: 0.92, letterSpacing: '-0.05em', color: brand.sandrift }}>
                a Hudson
              </h2>
            </div>
          </Reveal>

          <Reveal delay={0.16}>
            <div className="mt-12 lg:mt-14 xl:mt-16">
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={BOOKING_CALENDAR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-3 px-14 py-6 font-sans text-[15px] font-medium text-white tracking-[0.06em] uppercase transition-all duration-500 hover:brightness-110"
                  style={{ backgroundColor: brand.sandrift }}
                >
                  Schedule a Meeting <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                </a>
                <Link
                  to="/quiz"
                  className="group inline-flex items-center justify-center gap-3 px-14 py-6 font-sans text-[15px] font-medium tracking-[0.06em] uppercase transition-all duration-500 hover:opacity-70"
                  style={{ border: `1px solid ${brand.plaster}`, color: brand.drillbitSkies }}
                >
                  Discover Your Archetype <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
              <p className="text-sm mt-5 font-sans" style={{ color: brand.drillbitSkies }}>No commitment required. Cancel anytime.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
)

/* ══════════════════════════════════════════════════
   SUPPORTING ELEMENTS — Below the narrative spine
   ══════════════════════════════════════════════════ */
const FAQS = [
  { q: 'What makes Hudson different from a handyman?', a: "A handyman shows up when something breaks. Your Hudson shows up so nothing does. We\u2019re a dedicated home management professional \u2014 one person who knows every system and maintains it year-round. Think of it as a chief of staff for your home." },
  { q: 'When will the app be available?', a: "We\u2019re building it now. Members get early access. Complete visibility \u2014 service history, upcoming work, direct messaging \u2014 without requiring your involvement." },
  { q: "What\u2019s included in membership?", a: "One dedicated professional who manages everything: hands-on seasonal maintenance, bringing in licensed specialists when needed, emergency response, long-term planning, and annual supply restocking. Your Hudson owns the details. You get a summary." },
  { q: 'Can I try it before committing?', a: "Every membership starts with a 30-day money-back guarantee. If it doesn\u2019t work for you, we refund the full amount. No questions. We\u2019d rather earn your trust than lock you into a contract." },
] as const

const FAQ: React.FC = () => {
  const [open, setOpen] = useState<number | null>(null)
  return (
    <section className="relative overflow-hidden" style={{ backgroundColor: PAPER }}>
      {/* Full-bleed white emboss — modern home (hero uses colonial) */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden>
        <div className="absolute inset-[-10%] min-h-[120%] min-w-[120%]">
          <img
            src={img.houseEmbossModern}
            alt=""
            className="h-full w-full object-cover object-center"
            style={{ mixBlendMode: 'multiply', opacity: 0.13 }}
          />
        </div>
      </div>
      <div className="absolute top-0 left-0 right-0 h-32 z-[1]" style={{ background: `linear-gradient(to bottom, ${PAPER}, transparent)` }} aria-hidden />
      <div className="relative z-10 max-w-[800px] mx-auto px-6 md:px-10 lg:px-16 pt-36 md:pt-44 lg:pt-52 pb-28 md:pb-36 lg:pb-44">
        <Reveal>
          <div className="text-center mb-12">
            <h2 className="font-serif font-normal mb-4" style={{ fontSize: 'clamp(2.25rem, 4.5vw, 3.25rem)', lineHeight: 1.05, letterSpacing: '-0.02em', color: brand.tapeTurquoise }}>Common Questions</h2>
            <div className="w-16 h-px mx-auto" style={{ backgroundColor: brand.sandrift }} />
          </div>
        </Reveal>
        <div className="border-t" style={{ borderColor: `${brand.plaster}80` }}>
          {FAQS.map((faq, i) => (
            <Reveal key={faq.q} delay={i * 0.06}>
              <div className="border-b" style={{ borderColor: `${brand.plaster}80` }}>
                <button type="button" onClick={() => setOpen(open === i ? null : i)} className="flex w-full items-center justify-between gap-6 py-7 text-left">
                  <span className="font-sans text-lg font-medium" style={{ color: brand.tapeTurquoise }}>{faq.q}</span>
                  <span className={`shrink-0 text-2xl transition-transform duration-300 ${open === i ? 'rotate-45' : ''}`} style={{ color: brand.drillbitSkies }}>+</span>
                </button>
                <AnimatePresence>
                  {open === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35 }} className="overflow-hidden">
                      <p className="pb-7 font-sans text-base leading-[1.8]" style={{ color: brand.drillbitSkies }}>{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

const ClosingBand: React.FC = () => (
  <section className="relative overflow-hidden" style={{ backgroundColor: brand.tapeTurquoise }}>
    <div className="absolute top-0 left-0 right-0 h-64" style={{ background: `linear-gradient(to bottom, ${PAPER} 0%, ${PAPER}e0 20%, ${brand.tapeTurquoise}80 60%, ${brand.tapeTurquoise} 100%)` }} aria-hidden />
    <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse 70% 40% at 50% 25%, ${brand.utilityBlue}20, transparent)` }} aria-hidden />
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-48 lg:pt-56 pb-24 lg:pb-32">
      {/* Archetype + calendar — same pattern as hero */}
      <Reveal>
        <div className="text-center max-w-2xl mx-auto mb-24 lg:mb-32">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-6" style={{ color: brand.sandrift }}>Start Here</p>
          <h3 className="font-serif font-normal text-white mb-6" style={{ fontSize: 'clamp(2.25rem, 4.5vw, 3.25rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
            Meet on the calendar, or take the <span className="italic" style={{ color: brand.sandrift }}>60-second quiz</span> first
          </h3>
          <p className="font-sans text-base lg:text-lg text-white/75 leading-relaxed mb-10">
            Schedule a time that works for you, or discover your homeowner archetype &mdash; quick insights into how you manage your home and what tends to slip.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center">
            <MeetCalendarCta />
            <ArchetypeQuizCta lightOnDark />
          </div>
        </div>
      </Reveal>

      <div className="h-px w-full mb-16 lg:mb-20" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />

      {/* Referral + Service Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal>
          <h3 className="font-serif font-normal text-white" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            Know someone who runs everything at a high level &mdash; <span className="italic" style={{ color: brand.sandrift }}>except their house?</span>
          </h3>
          <p className="mt-4 font-sans text-base text-white/70 leading-relaxed">Send them to Hudson. When they join, you both get a free month.</p>
          <Link to="/refer" className="group mt-6 inline-flex items-center gap-2 font-sans text-sm font-medium tracking-[0.06em] uppercase transition-opacity duration-300 hover:opacity-70" style={{ color: brand.sandrift }}>
            Refer a Friend <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="lg:text-right">
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-3" style={{ color: brand.sandrift }}>Proudly Serving the Des Moines Metro</p>
            <p className="font-sans text-sm text-white/70">
              {['Ankeny', 'Urbandale', 'Johnston', 'West Des Moines', 'Waukee', 'Altoona', 'Ames'].map((c, i) => (
                <React.Fragment key={c}>{i > 0 && <span className="mx-1.5 opacity-40">&middot;</span>}<span>{c}</span></React.Fragment>
              ))}
            </p>
            <p className="mt-2 font-sans text-xs text-white/60">Expanding soon. <Link to="/membership" className="underline underline-offset-4 hover:opacity-70 transition-colors" style={{ color: brand.sandrift }}>Let us know</Link> if you want a Hudson in your area.</p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
)

/* ══════════════════════════════════════════════════
   PAGE ASSEMBLY — Strict StoryBrand sequence:
   1. Character (Hero)
   2. Problem (external, internal, philosophical)
   3. Guide (empathy + authority pillars)
   4. Plan (three steps + CTA + risk reversal)
   5. Failure (cost of inaction)
   6. Call to Action (service cards — choose your path)
   7. Success (transformation + testimonial)
   8. Final CTA (close)
   — Supporting: FAQ, Referral, Service Area, Snapshot Teaser
   ══════════════════════════════════════════════════ */
const HomePage: React.FC = () => (
  <div style={{ backgroundColor: brand.tapeTurquoise }}>
    <MetaTags
      title="Hudson My Home | We Maintain Your Home So You Don't Have To"
      description="We maintain your home so you don't have to. One dedicated professional manages your entire home — hands-on maintenance, specialist work when it's needed, emergencies, and everything in between."
    />
    <Navbar />
    <HudsonOfferBanner />
    <main>
      <Hero />
      <Problem />
      <Guide />
      <SketchBreak bg={PAPER} />
      <Plan />
      <Failure />
      <ServiceCards />
      <Success />
      <FinalCTA />
      <FAQ />
      <ClosingBand />
    </main>
    <Footer />
  </div>
)

export default HomePage
